import type {
  PipelineBatchResult,
  ValidationIssue,
} from '../../../../../../governance'
import type { GroupId } from '../../../../../../config/systemConfig'

/**
 * computeAssessmentPoolHealth — derivation for Stage 6b (S8 / FG-AssessmentPool).
 *
 * Walks the batch result, collects every issue whose `domain === 'assessmentPool'`,
 * deduplicates them on a composite key (the same finding is attached to a
 * representative question and may appear once per affected slice), and splits
 * them into two view-models the card consumes:
 *
 *  - `slices`: Layer A (capacity) + Layer B (distribution), keyed on
 *              `${module}::${tierBand}`. Sorted by urgency.
 *  - `feasibility`: Layer C (blueprint dry-run), keyed on
 *                   `${group}::${module}`. Sorted by urgency.
 *
 * Pure / deterministic — given the same batch result, returns the same report.
 */

// ─── View-model types (the card's public contract) ──────────────────────────

export type AssessmentPoolCapacityStatus = 'gap' | 'thin' | 'ok'
export type AssessmentPoolDistributionReason =
  | 'cito_concentration'
  | 'dok_coverage'
export type AssessmentPoolFeasibilityReason =
  | 'zero_questions'
  | 'shortfall'
  | 'degraded'

export interface AssessmentPoolSliceFinding {
  /** `${module}::${tierBand}` */
  sliceKey: string
  module: string
  tierBand: string
  capacityStatus: AssessmentPoolCapacityStatus
  /** Distribution findings present on this slice, if any. */
  distributionReasons: AssessmentPoolDistributionReason[]
  /** Raw issues backing this slice (capacity + distribution). */
  issues: ValidationIssue[]
  mcEligibleCount?: number
  minimumViableSize?: number
  comfortSize?: number
  remediationNeeded?: number
}

export interface AssessmentPoolFeasibilityFinding {
  /** `${group}::${module}` */
  comboKey: string
  group: GroupId
  module: string
  reason: AssessmentPoolFeasibilityReason
  issue: ValidationIssue
  target?: number
  achievable?: number
  bandAchievable?: { low: number; mid: number; high: number }
  bandTarget?: { low: number; mid: number; high: number }
  emptyBands?: string[]
}

export interface AssessmentPoolHealthReport {
  /** Total slices (module × tierBand) with at least one Layer A/B finding. */
  affectedSlices: number
  slices: AssessmentPoolSliceFinding[]
  feasibility: AssessmentPoolFeasibilityFinding[]
  /** Count of affected slices per module — used for the per-module bar list. */
  byModule: Record<string, number>
  capacityCounts: { gap: number; thin: number }
  distributionCounts: { citoConcentration: number; dokCoverage: number }
  feasibilityCounts: {
    zeroQuestions: number
    shortfall: number
    degraded: number
  }
}

// ─── Internal helpers ───────────────────────────────────────────────────────

function readString(
  meta: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  if (!meta) return undefined
  const v = meta[key]
  return typeof v === 'string' ? v : undefined
}

function readNumber(
  meta: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  if (!meta) return undefined
  const v = meta[key]
  return typeof v === 'number' ? v : undefined
}

function readBands(
  meta: Record<string, unknown> | undefined,
  key: string,
): { low: number; mid: number; high: number } | undefined {
  if (!meta) return undefined
  const v = meta[key]
  if (!v || typeof v !== 'object') return undefined
  const obj = v as Record<string, unknown>
  if (
    typeof obj.low === 'number' &&
    typeof obj.mid === 'number' &&
    typeof obj.high === 'number'
  ) {
    return { low: obj.low, mid: obj.mid, high: obj.high }
  }
  return undefined
}

function readStringArray(
  meta: Record<string, unknown> | undefined,
  key: string,
): string[] | undefined {
  if (!meta) return undefined
  const v = meta[key]
  if (!Array.isArray(v)) return undefined
  return v.filter((x): x is string => typeof x === 'string')
}

/** Capacity-status ordering: gap (worst) > thin > ok. */
const CAPACITY_RANK: Record<AssessmentPoolCapacityStatus, number> = {
  gap: 2,
  thin: 1,
  ok: 0,
}

/** Feasibility ordering: zero_questions (worst) > shortfall > degraded. */
const FEASIBILITY_RANK: Record<AssessmentPoolFeasibilityReason, number> = {
  zero_questions: 3,
  shortfall: 2,
  degraded: 1,
}

// ─── Main entry point ───────────────────────────────────────────────────────

/**
 * Build an AssessmentPoolHealthReport from a pipeline batch result.
 * Returns an empty (all-zero) report when no findings or no batch yet.
 */
export function computeAssessmentPoolHealth(
  batchResult: PipelineBatchResult | null | undefined,
): AssessmentPoolHealthReport {
  const emptyReport: AssessmentPoolHealthReport = {
    affectedSlices: 0,
    slices: [],
    feasibility: [],
    byModule: {},
    capacityCounts: { gap: 0, thin: 0 },
    distributionCounts: { citoConcentration: 0, dokCoverage: 0 },
    feasibilityCounts: { zeroQuestions: 0, shortfall: 0, degraded: 0 },
  }
  if (!batchResult) return emptyReport

  // Composite-key dedupe maps. Batch issues are attached to a representative
  // question id and surface in `result.gate.issues` for that question — but
  // the orchestrator may attribute a single finding to multiple results.
  const seenSliceKeys = new Set<string>()
  const seenFeasibilityKeys = new Set<string>()

  const slicesByKey = new Map<string, AssessmentPoolSliceFinding>()
  const feasibilityByKey = new Map<string, AssessmentPoolFeasibilityFinding>()

  for (const result of batchResult.results) {
    for (const issue of result.gate.issues) {
      if (issue.domain !== 'assessmentPool') continue
      const meta = (issue.metadata ?? {}) as Record<string, unknown>

      if (
        issue.code === 'ASSESSMENT_POOL_CAPACITY' ||
        issue.code === 'ASSESSMENT_POOL_DISTRIBUTION'
      ) {
        const module = readString(meta, 'module')
        const tierBand = readString(meta, 'tierBand')
        if (!module || !tierBand) continue
        const sliceKey = `${module}::${tierBand}`
        const reason = readString(meta, 'reason')
        const status = readString(meta, 'status') as
          | AssessmentPoolCapacityStatus
          | undefined

        // Dedupe key includes code + reason to keep capacity and distribution
        // findings separate within the same slice (one slice can have both).
        const dedupeKey = `${sliceKey}::${issue.code}::${reason ?? ''}`
        if (seenSliceKeys.has(dedupeKey)) continue
        seenSliceKeys.add(dedupeKey)

        let slice = slicesByKey.get(sliceKey)
        if (!slice) {
          slice = {
            sliceKey,
            module,
            tierBand,
            capacityStatus: 'ok',
            distributionReasons: [],
            issues: [],
          }
          slicesByKey.set(sliceKey, slice)
        }
        slice.issues.push(issue)

        if (issue.code === 'ASSESSMENT_POOL_CAPACITY') {
          if (status === 'gap' || status === 'thin') {
            // Keep the strictest status if multiple capacity issues arrive.
            if (CAPACITY_RANK[status] > CAPACITY_RANK[slice.capacityStatus]) {
              slice.capacityStatus = status
            }
          }
          slice.mcEligibleCount =
            readNumber(meta, 'mcEligibleCount') ?? slice.mcEligibleCount
          slice.minimumViableSize =
            readNumber(meta, 'minimumViableSize') ?? slice.minimumViableSize
          slice.comfortSize =
            readNumber(meta, 'comfortSize') ?? slice.comfortSize
          const remediation = meta.remediation as
            | Record<string, unknown>
            | undefined
          if (remediation && typeof remediation.needed === 'number') {
            slice.remediationNeeded = remediation.needed
          }
        } else if (issue.code === 'ASSESSMENT_POOL_DISTRIBUTION') {
          if (
            (reason === 'cito_concentration' || reason === 'dok_coverage') &&
            !slice.distributionReasons.includes(reason)
          ) {
            slice.distributionReasons.push(reason)
          }
        }
        continue
      }

      if (issue.code === 'ASSESSMENT_POOL_FEASIBILITY') {
        const module = readString(meta, 'module')
        const groupRaw = meta.group
        if (!module || typeof groupRaw !== 'number') continue
        const group = groupRaw as GroupId
        const reason = readString(meta, 'reason') as
          | AssessmentPoolFeasibilityReason
          | undefined
        if (
          reason !== 'zero_questions' &&
          reason !== 'shortfall' &&
          reason !== 'degraded'
        ) {
          continue
        }
        const comboKey = `${group}::${module}`
        const dedupeKey = `${comboKey}::${reason}`
        if (seenFeasibilityKeys.has(dedupeKey)) continue
        seenFeasibilityKeys.add(dedupeKey)

        feasibilityByKey.set(comboKey, {
          comboKey,
          group,
          module,
          reason,
          issue,
          target: readNumber(meta, 'target'),
          achievable: readNumber(meta, 'achievable'),
          bandAchievable: readBands(meta, 'bandAchievable'),
          bandTarget: readBands(meta, 'bandTarget'),
          emptyBands: readStringArray(meta, 'emptyBands'),
        })
      }
    }
  }

  // ─── Aggregate counters ────────────────────────────────────────────────
  const slices = Array.from(slicesByKey.values())
  const feasibility = Array.from(feasibilityByKey.values())

  let gap = 0
  let thin = 0
  let citoConcentration = 0
  let dokCoverage = 0
  const byModule: Record<string, number> = {}

  for (const slice of slices) {
    if (slice.capacityStatus === 'gap') gap++
    else if (slice.capacityStatus === 'thin') thin++
    if (slice.distributionReasons.includes('cito_concentration')) {
      citoConcentration++
    }
    if (slice.distributionReasons.includes('dok_coverage')) {
      dokCoverage++
    }
    byModule[slice.module] = (byModule[slice.module] ?? 0) + 1
  }

  let zeroQuestions = 0
  let shortfall = 0
  let degraded = 0
  for (const f of feasibility) {
    if (f.reason === 'zero_questions') zeroQuestions++
    else if (f.reason === 'shortfall') shortfall++
    else if (f.reason === 'degraded') degraded++
  }

  // ─── Sort by urgency ───────────────────────────────────────────────────
  // Slices: capacity rank desc, then number of distribution reasons desc,
  // then module asc + tierBand asc for stable ordering.
  slices.sort((a, b) => {
    const capDiff =
      CAPACITY_RANK[b.capacityStatus] - CAPACITY_RANK[a.capacityStatus]
    if (capDiff !== 0) return capDiff
    const distDiff = b.distributionReasons.length - a.distributionReasons.length
    if (distDiff !== 0) return distDiff
    if (a.module !== b.module) return a.module.localeCompare(b.module)
    return a.tierBand.localeCompare(b.tierBand)
  })

  feasibility.sort((a, b) => {
    const rDiff = FEASIBILITY_RANK[b.reason] - FEASIBILITY_RANK[a.reason]
    if (rDiff !== 0) return rDiff
    if (a.group !== b.group) return a.group - b.group
    return a.module.localeCompare(b.module)
  })

  return {
    affectedSlices: slices.length,
    slices,
    feasibility,
    byModule,
    capacityCounts: { gap, thin },
    distributionCounts: { citoConcentration, dokCoverage },
    feasibilityCounts: { zeroQuestions, shortfall, degraded },
  }
}
